import _ from 'lodash';
import indicatorConfig from '../config/indicators.json';
import {DATA_SETS} from '../Constants';
import {
  GRASS_KEY_EO_GROS,
  GRASS_KEY_GZD_GROS,
  GRASS_KEY_NAT_GROS
} from '../reducers/point_data_grass';
import {convertedUnits} from './point_utils';

/** Curated ISP checklist: merged weather + AFDRS indicators + Mk5 + grass outputs (not a separate model). */
export const ISP_MK5_FROS_KMH_KEY = 'ISP_MK5_FROS_KMH';

/** Grass behaviour outputs (not GeoServer layers); order matches `isp_order` between AFDRS flame and spotting. */
const GRASS_ISP = [
  { isp_order: 92, grassKey: GRASS_KEY_NAT_GROS },
  { isp_order: 93, grassKey: GRASS_KEY_GZD_GROS },
  { isp_order: 94, grassKey: GRASS_KEY_EO_GROS }
];

/** McArthur Mk5 FROS as km/h (derived); sits between AFDRS flame (90) and grass (92–94). */
const MK5_FROS_ISP_ORDER = 91;

/** First `isp_order` for fire-behaviour outputs (FBI / AFDRS / grass / Mk5); below this = weather block. */
const ISP_ORDER_FIRE_START = 70;

/** Table header background: weather vs fire (fire matches `PointGraphs.css` default #9cbcd3 for indicator headers). */
const ISP_HEADER_COLOR_WEATHER = '#67a1ea';
const ISP_HEADER_COLOR_FIRE = '#9cbcd3';

function metaFromIndicatorConfig(ind) {
  return {
    key: ind.prefix,
    only_in_app_mode: ind.only_in_app_mode,
    displayName: (ind.tableDisplayName || ind.displayName) + convertedUnits(ind),
    editable: !!ind.editable,
    column_editable: !!ind.column_editable,
    hide_in_table: !!ind.hide_in_table,
    use_import_time_over_creation_time: !!ind.use_import_time_over_creation_time,
    exclude_from_age_checks: !!ind.exclude_from_age_checks,
    max: ind.max,
    min: ind.min,
    decPlaces: ind.rounding_rule ? parseInt(ind.rounding_rule, 10) : 0,
    colors: ind.colors,
    dataSet: ind.prefix === 'HZ_OVERALL' ? DATA_SETS.ISP : DATA_SETS.WEATHER,
    headerColor: ISP_HEADER_COLOR_WEATHER,
    arrow: ind.arrow
  };
}

function resolveIndicatorColumnMeta(ind, byKey) {
  const existing = byKey[ind.prefix];
  if (existing) {
    return existing;
  }
  return metaFromIndicatorConfig(ind);
}

/**
 * Prefixes with point samples, for export “data updated” header (see `include_in_isp` in `indicators.json`).
 */
export function getIspExportDateRangeKeys() {
  return indicatorConfig
  .filter(i => i.include_in_isp && i.point_data)
  .sort((a, b) => (a.isp_order || 0) - (b.isp_order || 0))
  .map(i => i.prefix);
}

/**
 * @param {Array} weatherTableMetaData from point_data_weather
 * @param {Array} grassTableMetaData from point_data_grass
 */
export function getIspColumnMetadata(weatherTableMetaData, grassTableMetaData) {
  const byKey = _.keyBy(weatherTableMetaData, 'key');
  const grassByKey = _.keyBy(grassTableMetaData, 'key');

  const ispIndicators = indicatorConfig
  .filter(i => i.include_in_isp)
  .sort((a, b) => (a.isp_order || 0) - (b.isp_order || 0));

  const entries = ispIndicators.map(ind => ({
    order: ind.isp_order,
    meta: () => {
      const m = resolveIndicatorColumnMeta(ind, byKey);
      if (!m) {
        return null;
      }
      const fireSection = (ind.isp_order || 0) >= ISP_ORDER_FIRE_START;
      return { ...m, headerColor: fireSection ? ISP_HEADER_COLOR_FIRE : ISP_HEADER_COLOR_WEATHER };
    }
  }));

  entries.push({
    order: MK5_FROS_ISP_ORDER,
    meta: () => ({
      key: ISP_MK5_FROS_KMH_KEY,
      displayName: 'McArthur Mk5 FROS (km/h)',
      decPlaces: 2,
      dataSet: DATA_SETS.ISP,
      headerColor: ISP_HEADER_COLOR_FIRE,
      exclude_from_age_checks: true
    })
  });

  GRASS_ISP.forEach(({ isp_order, grassKey }) => {
    entries.push({
      order: isp_order,
      meta: () => {
        const m = grassByKey[grassKey];
        return m ? { ...m, headerColor: ISP_HEADER_COLOR_FIRE } : null;
      }
    });
  });

  return _.sortBy(entries, 'order')
          .map(e => e.meta())
          .filter(Boolean);
}

export function buildIspTableData(weatherTableData, mk5TableData, grassTableData) {
  const out = {};
  _.each(weatherTableData, (wRow, name) => {
    const mRow = mk5TableData[name];
    const gRow = grassTableData[name];
    const row = _.clone(wRow);
    if (mRow && typeof mRow.FROS === 'number' && !isNaN(mRow.FROS)) {
      row[ISP_MK5_FROS_KMH_KEY] = mRow.FROS / 1000;
    }
    if (gRow) {
      GRASS_ISP.forEach(({ grassKey }) => {
        if (typeof gRow[grassKey] !== 'undefined') {
          row[grassKey] = gRow[grassKey];
        }
      });
    }
    out[name] = row;
  });
  return out;
}



// WEBPACK FOOTER //
// ./src/utils/isp_dataset.js